import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Coins, LogOut, Wallet, TrendingDown, Megaphone, MessageCircle, Send, Banknote, Lock } from "lucide-react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

const fmt = (x) => Number(x || 0).toFixed(2);
const fmtDT = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
function filterByRange(list, mode, dateKey = "created_at") {
  if (mode === "all") return list;
  const today = startOfDay(new Date());
  const yest = new Date(today); yest.setDate(today.getDate()-1);
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate()-6);
  return list.filter((it) => {
    const d = new Date(it[dateKey]);
    if (mode === "today") return d >= today;
    if (mode === "yesterday") return d >= yest && d < today;
    if (mode === "week") return d >= weekAgo;
    return true;
  });
}
function DayFilterBar({ value, onChange }) {
  const opts = [["today","Today"],["yesterday","Yesterday"],["week","Last 7 days"]];
  return (
    <div className="flex gap-1 mb-3" data-testid="day-filter">
      {opts.map(([k,l]) => (
        <button key={k} onClick={()=>onChange(k)} data-testid={`day-filter-${k}`}
          className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${value===k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [balance, setBalance] = useState({ coin_balance: 0, exposure: 0, withdrawal_locked: 0 });
  const [events, setEvents] = useState([]);
  const [txs, setTxs] = useState([]);
  const [bets, setBets] = useState([]);
  const [rules, setRules] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState("upi");
  const [withdrawPay, setWithdrawPay] = useState({ upi_id: "", account_holder: "", account_number: "", ifsc: "", bank_name: "", notes: "" });
  const [betSubmitting, setBetSubmitting] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  // Live clock ticks every second; used to derive open/closed and remaining time from real event timestamps.
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const [modal, setModal] = useState(null); // { event, team, amount, edit?: bet }
  const [showExposure, setShowExposure] = useState(false);
  const [betFilter, setBetFilter] = useState("week");
  const [txFilter, setTxFilter] = useState("week");
  const [rulesModal, setRulesModal] = useState(false);
  const [rulesAgree, setRulesAgree] = useState(false);
  const [annPopup, setAnnPopup] = useState(false);
  const [annOpen, setAnnOpen] = useState(false);
  const { refresh } = useAuth();

  // Track which lazy tabs have been loaded at least once.
  // Bet Statement + Coin Statement are heavy → fetched on first tab open only.
  const [loadedTabs, setLoadedTabs] = useState({ mybets: false, coins: false });
  const [activeTab, setActiveTab] = useState("events");

  const loadAll = async () => {
    try {
      const [b, e, r, a, w] = await Promise.all([
        api.get("/user/balance"),
        api.get("/user/events"),
        api.get("/user/rules"),
        api.get("/user/announcements"),
        api.get("/user/withdrawals"),
      ]);
      setBalance(b.data); setEvents(e.data);
      setRules(r.data.rules || ""); setAnnouncements(a.data);
      setWithdrawals(w.data);
    } catch (err) { toast.error(formatError(err)); }
  };

  // Narrow refetchers. Statement refetchers are cache-aware: they skip if the tab was never opened.
  const refetchBalance = async () => {
    try { const { data } = await api.get("/user/balance"); setBalance(data); } catch (_e) { /* silent */ }
  };
  const refetchEvents = async () => {
    try { const { data } = await api.get("/user/events"); setEvents(data); } catch (_e) { /* silent */ }
  };
  const refetchBetStatement = async ({ force = false } = {}) => {
    if (!force && !loadedTabs.mybets) return; // never opened → don't fetch, wait for user
    try { const { data } = await api.get("/user/bet-statement"); setBets(data); } catch (_e) { /* silent */ }
  };
  const refetchCoinStatement = async ({ force = false } = {}) => {
    if (!force && !loadedTabs.coins) return;
    try { const { data } = await api.get("/user/coin-statement"); setTxs(data); } catch (_e) { /* silent */ }
  };
  const refetchWithdrawals = async () => {
    try { const { data } = await api.get("/user/withdrawals"); setWithdrawals(data); } catch (_e) { /* silent */ }
  };

  const onTabChange = (v) => {
    setActiveTab(v);
    if (v === "mybets" && !loadedTabs.mybets) {
      setLoadedTabs((s) => ({ ...s, mybets: true }));
      refetchBetStatement({ force: true });
    }
    if (v === "coins" && !loadedTabs.coins) {
      setLoadedTabs((s) => ({ ...s, coins: true }));
      refetchCoinStatement({ force: true });
    }
  };

  useEffect(() => { loadAll(); refresh?.(); }, []);

  // First-time rules acceptance (blocks dashboard)
  useEffect(() => {
    if (user && user.role === "user" && !user.rules_accepted_at) {
      setRulesModal(true);
    }
  }, [user]);

  // Auto-popup announcements: show if any active one hasn't been dismissed
  useEffect(() => {
    if (!user || !announcements.length) return;
    const seenKey = `tk_ann_seen_${user.id}`;
    const seen = new Set(JSON.parse(localStorage.getItem(seenKey) || "[]"));
    const unseen = announcements.filter(a => !seen.has(a.id));
    if (unseen.length > 0) setAnnPopup(true);
  }, [announcements, user]);

  const closeAnnPopup = () => {
    if (user) {
      const seenKey = `tk_ann_seen_${user.id}`;
      const seen = new Set(JSON.parse(localStorage.getItem(seenKey) || "[]"));
      announcements.forEach((a) => seen.add(a.id));
      localStorage.setItem(seenKey, JSON.stringify([...seen]));
    }
    setAnnPopup(false);
  };

  const acceptRules = async () => {
    try {
      await api.post("/user/accept-rules");
      toast.success("Welcome! You may now play.");
      setRulesModal(false);
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const openBet = (event, team, edit=null) => setModal({ event, team, amount: edit?.amount || "", edit });

  const placeBet = async () => {
    if (betSubmitting) return;
    const amount = parseFloat(modal.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setBetSubmitting(true);
    try {
      if (modal.edit) {
        await api.patch(`/user/bets/${modal.edit.id}`, { team: modal.team, amount });
        toast.success("Bet updated");
      } else {
        await api.post("/user/bets", { event_id: modal.event.id, team: modal.team, amount });
        toast.success("Bet placed");
      }
      setModal(null);
      // Bet place/edit affects: balance (debit), events (my_bet updates), bet-statement (new row / updated row).
      // Coin-statement, rules, announcements, withdrawals unchanged.
      Promise.all([refetchBalance(), refetchEvents(), refetchBetStatement(), refetchCoinStatement()]);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBetSubmitting(false); }
  };

  const cancelBet = async () => {
    if (!modal?.edit) return;
    if (!window.confirm("Cancel this bet and refund the stake?")) return;
    try {
      await api.delete(`/user/bets/${modal.edit.id}`);
      toast.success("Bet cancelled — stake refunded");
      setModal(null);
      // Cancel affects: balance (refund), events (my_bet cleared), bet-statement (status→cancelled), coin-statement (refund tx).
      Promise.all([refetchBalance(), refetchEvents(), refetchBetStatement(), refetchCoinStatement()]);
    } catch (err) { toast.error(formatError(err)); }
  };

  const addPreset = (v) => {
    const cur = parseFloat(modal?.amount) || 0;
    setModal({ ...modal, amount: String(cur + v) });
  };
  const clearAmount = () => setModal({ ...modal, amount: "" });

  const submitWithdrawal = async () => {
    if (withdrawSubmitting) return;
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (amount > Number(balance.coin_balance || 0)) { toast.error("Amount exceeds your balance"); return; }
    if (withdrawMethod === "upi" && !withdrawPay.upi_id.trim()) { toast.error("UPI ID is required"); return; }
    if (withdrawMethod === "bank" && (!withdrawPay.account_holder.trim() || !withdrawPay.account_number.trim() || !withdrawPay.ifsc.trim())) {
      toast.error("Account holder, number and IFSC are required"); return;
    }
    if (withdrawMethod === "other" && !withdrawPay.notes.trim()) { toast.error("Please describe your withdrawal method"); return; }
    setWithdrawSubmitting(true);
    try {
      await api.post("/user/withdrawals", { amount, method: withdrawMethod, ...withdrawPay });
      toast.success("Withdrawal request submitted for admin approval");
      setWithdrawModal(false); setWithdrawAmount("");
      setWithdrawPay({ upi_id: "", account_holder: "", account_number: "", ifsc: "", bank_name: "", notes: "" });
      // Withdrawal affects: balance (locked+/balance−), withdrawals list, coin-statement (request tx).
      // Events, bets, rules, announcements unchanged.
      Promise.all([refetchBalance(), refetchWithdrawals(), refetchCoinStatement()]);
    } catch (err) { toast.error(formatError(err)); }
    finally { setWithdrawSubmitting(false); }
  };

  const doLogout = () => { logout(); nav("/login"); };

  return (
    <div className="bet-theme min-h-screen bg-background text-foreground">
      <header className="bet-header sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3" data-testid="user-brand-header">
              {user?.brand_logo_url ? (
                <img src={user.brand_logo_url} alt="logo" className="h-24 w-24 md:h-20 md:w-20 rounded-2xl object-cover border-2 border-white shadow-xl flex-shrink-0" />
              ) : (
                <div className="h-12 w-12 md:h-10 md:w-10 rounded bet-pill grid place-items-center"><Coins className="h-6 w-6 md:h-5 md:w-5"/></div>
              )}
              <div className="min-w-0">
              <div className="font-display font-bold text-white text-xl md:text-lg truncate" data-testid="user-brand-name">{user?.brand_name || "TossKing"}</div>
                <div className="text-xs text-blue-100 truncate">{user?.username}</div>
              </div>
            </div>
            <div className="grid grid-cols-4 md:flex md:items-center gap-2">
              <div className="text-center md:text-right border border-border rounded-lg px-2 md:px-3 py-1.5 bg-card" data-testid="balance-tile">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1 justify-center md:justify-end"><Wallet className="h-3 w-3"/><span className="hidden md:inline">Balance</span></div>
                <div className="mono font-bold text-sm" data-testid="user-balance">{fmt(balance.coin_balance)}</div>
              </div>
              <button type="button" onClick={()=>setShowExposure(true)} className="text-center md:text-right border border-border rounded-lg px-2 md:px-3 py-1.5 bg-card hover:border-primary btn-push cursor-pointer" data-testid="exposure-btn">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1 justify-center md:justify-end"><TrendingDown className="h-3 w-3"/><span className="hidden md:inline">Exposure</span></div>
                <div className="mono font-bold text-orange-500 text-sm" data-testid="user-exposure">{fmt(balance.exposure)}</div>
              </button>
              <button type="button" onClick={()=>setAnnOpen(true)} className="relative border border-border rounded-lg py-1.5 bg-card hover:border-primary btn-push grid place-items-center" data-testid="ann-bell-btn" title="Announcements">
                <Megaphone className="h-5 w-5 text-primary" />
                {announcements.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" data-testid="ann-badge">{announcements.length}</span>
                )}
              </button>
              <button type="button" onClick={doLogout} className="border border-border rounded-lg py-1.5 bg-card hover:border-primary btn-push grid place-items-center" data-testid="logout-btn" title="Logout">
                <LogOut className="h-5 w-5"/>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">

       <Tabs value={activeTab} onValueChange={onTabChange}>
  <div className="overflow-x-auto">
    <TabsList className="mb-4 inline-flex whitespace-nowrap min-w-max">

            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="mybets" data-testid="tab-mybets">My Bets</TabsTrigger>
            <TabsTrigger value="wallet" data-testid="tab-withdrawal">Withdrawal</TabsTrigger>
            <TabsTrigger value="coins" data-testid="tab-coins">Coin Statement</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
          </TabsList>
                </div>

          <TabsContent value="events" className="space-y-4">
            {events
              .filter((ev) => new Date(ev.end_time).getTime() > nowTs)
              .map((ev) => {
                const startMs = new Date(ev.start_time).getTime();
                const endMs = new Date(ev.end_time).getTime();
                const bettingOpen = startMs <= nowTs && nowTs <= endMs && ev.status === "open";
                const msLeft = Math.max(0, endMs - nowTs);
                const s = Math.floor(msLeft / 1000);
                const hh = Math.floor(s / 3600);
                const mm = Math.floor((s % 3600) / 60);
                const ss = s % 60;
                const countdown = hh > 0
                  ? `${hh}h ${String(mm).padStart(2,"0")}m ${String(ss).padStart(2,"0")}s`
                  : `${String(mm).padStart(2,"0")}:${String(ss).padStart(2,"0")}`;
                const preStart = nowTs < startMs;
                return (
              <Card key={ev.id} data-testid={`user-event-${ev.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="font-display">{ev.team1} <span className="text-muted-foreground text-base">vs</span> {ev.team2}</CardTitle>
                      <CardDescription className="mono text-xs">Odds {ev.odds} · Closes {fmtDT(ev.end_time)}</CardDescription>
                    </div>
                    {bettingOpen ? <Badge className="bg-green-600">OPEN</Badge> : preStart ? <Badge variant="outline">STARTS SOON</Badge> : <Badge variant="secondary">CLOSED</Badge>}
                  </div>
                  <div className="mt-2 flex items-center gap-2" data-testid={`countdown-${ev.id}`}>
                    <span className="text-xs text-muted-foreground">
                      {preStart ? "Betting opens in" : "Cutoff in"}
                    </span>
                    <span className={`mono text-sm font-bold ${msLeft < 60_000 && !preStart ? "text-red-500" : "text-primary"}`}>
                      {preStart
                        ? (() => {
                            const d = Math.max(0, startMs - nowTs);
                            const ds = Math.floor(d / 1000);
                            const dh = Math.floor(ds / 3600);
                            const dm = Math.floor((ds % 3600) / 60);
                            const dss = ds % 60;
                            return dh > 0 ? `${dh}h ${String(dm).padStart(2,"0")}m ${String(dss).padStart(2,"0")}s` : `${String(dm).padStart(2,"0")}:${String(dss).padStart(2,"0")}`;
                          })()
                        : countdown}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "team1", name: ev.team1 },
                      { key: "team2", name: ev.team2 },
                    ].map((t) => {
                      const mine = ev.my_bet && ev.my_bet.team === t.key;
                      return (
                        <button
                          key={t.key}
                          disabled={!bettingOpen}
                          onClick={()=>openBet(ev, t.key, ev.my_bet || null)}
                          data-testid={`bet-btn-${ev.id}-${t.key}`}
                          className={`text-left rounded-xl border p-4 btn-push disabled:opacity-50 disabled:cursor-not-allowed ${mine ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/50"}`}
                        >
                          <div className="text-xs text-muted-foreground">Bet on</div>
                          <div className="font-display font-bold text-xl">{t.name}</div>
                          <div className="mono text-2xl font-bold text-primary mt-2">{ev.odds}</div>
                          {mine && <div className="text-xs mt-2 text-primary font-semibold">You bet {fmt(ev.my_bet.amount)} · win {fmt(ev.my_bet.potential_win)}</div>}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              );
              })}
            {events.filter((ev) => new Date(ev.end_time).getTime() > nowTs).length===0 && <div className="text-muted-foreground text-center py-16">No active events right now. Check back soon.</div>}
          </TabsContent>

          <TabsContent value="mybets">
            <Card>
              <CardHeader><CardTitle>My Bets</CardTitle><CardDescription>Records auto-clear after 7 days.</CardDescription></CardHeader>
              <CardContent>
                <DayFilterBar value={betFilter} onChange={setBetFilter} />
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Match</TableHead><TableHead>Team</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">P.Win</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {filterByRange(bets, betFilter).map((b)=>(
                      <TableRow key={b.id}>
                        <TableCell className="mono text-xs">{fmtDT(b.created_at)}</TableCell>
                        <TableCell>{b.event_team1} vs {b.event_team2}</TableCell>
                        <TableCell>{b.team === "team1" ? b.event_team1 : b.event_team2}</TableCell>
                        <TableCell className="text-right mono">{fmt(b.amount)}</TableCell>
                        <TableCell className="text-right mono">{fmt(b.potential_win)}</TableCell>
                        <TableCell><Badge variant={b.status==="won"?"default":b.status==="lost"?"destructive":"secondary"}>{b.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                    {filterByRange(bets, betFilter).length===0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No bets in this range</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="coins">
            <Card>
              <CardHeader><CardTitle>Coin Statement</CardTitle><CardDescription>Records auto-clear after 7 days.</CardDescription></CardHeader>
              <CardContent>
                <DayFilterBar value={txFilter} onChange={setTxFilter} />
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Comment</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {filterByRange(txs, txFilter).map((t)=>(
                      <TableRow key={t.id}>
                        <TableCell className="mono text-xs">{fmtDT(t.created_at)}</TableCell>
                        <TableCell><Badge variant="outline">{t.type}</Badge></TableCell>
                        <TableCell className={`text-right mono ${t.amount>=0?"text-green-500":"text-red-500"}`}>{fmt(t.amount)}</TableCell>
                        <TableCell className="text-right mono">{fmt(t.balance_after)}</TableCell>
                        <TableCell className="text-xs">{t.comment}</TableCell>
                      </TableRow>
                    ))}
                    {filterByRange(txs, txFilter).length===0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No transactions in this range</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="wallet">
            <Card>
              <CardHeader>
                <CardTitle>Withdrawal</CardTitle>
                <CardDescription>Request coin withdrawals and track approval status.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="border border-border rounded-lg p-3 bg-card" data-testid="wallet-balance">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="h-3 w-3"/>Balance</div>
                    <div className="mono font-bold text-xl">{fmt(balance.coin_balance)}</div>
                  </div>
                  <div className="border border-border rounded-lg p-3 bg-card" data-testid="wallet-exposure">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3 w-3"/>Exposure</div>
                    <div className="mono font-bold text-xl text-orange-500">{fmt(balance.exposure)}</div>
                  </div>
                  <div className="border border-border rounded-lg p-3 bg-card" data-testid="wallet-locked">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3"/>Locked (Withdrawal)</div>
                    <div className="mono font-bold text-xl text-blue-500">{fmt(balance.withdrawal_locked)}</div>
                  </div>
                </div>
                <Button onClick={()=>{ setWithdrawAmount(""); setWithdrawModal(true); }} className="btn-push" data-testid="open-withdraw-btn">
                  <Banknote className="h-4 w-4 mr-2"/>Request Withdrawal
                </Button>
                <div>
                  <div className="font-semibold mb-2 text-sm">Withdrawal History</div>
                  <Table>
                    <TableHeader><TableRow><TableHead>Requested</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>Resolved</TableHead><TableHead>Admin comment</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {withdrawals.map((w)=>(
                        <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                          <TableCell className="mono text-xs">{fmtDT(w.created_at)}</TableCell>
                          <TableCell className="text-right mono">{fmt(w.amount)}</TableCell>
                          <TableCell><Badge variant="outline" className="uppercase text-[10px]">{w.payment?.method || "—"}</Badge></TableCell>
                          <TableCell><Badge variant={w.status==="approved"?"default":w.status==="rejected"?"destructive":"secondary"}>{w.status}</Badge></TableCell>
                          <TableCell className="mono text-xs">{w.resolved_at ? fmtDT(w.resolved_at) : "—"}</TableCell>
                          <TableCell className="text-xs">{w.comment || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {withdrawals.length===0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No withdrawal requests yet</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rules">
            <Card>
              <CardHeader><CardTitle>Rules of the Book</CardTitle></CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap text-sm text-foreground/90 font-sans" data-testid="rules-content">{rules || "No rules set by admin yet."}</pre>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!modal} onOpenChange={(o)=>!o&&setModal(null)}>
        <DialogContent data-testid="bet-modal">
          {modal && (
            <>
              <DialogHeader><DialogTitle>{modal.edit ? "Edit Bet" : "Place Bet"} · {modal.event.team1} vs {modal.event.team2}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {["team1","team2"].map((k)=>(
                    <button key={k} onClick={()=>setModal({...modal, team:k})}
                      data-testid={`modal-team-${k}`}
                      className={`rounded-lg border p-3 text-left btn-push ${modal.team===k?"border-primary bg-primary/10":"border-border"}`}>
                      <div className="text-xs text-muted-foreground">Team</div>
                      <div className="font-display font-bold">{k==="team1"?modal.event.team1:modal.event.team2}</div>
                    </button>
                  ))}
                </div>
                <div>
                  <Label>Coins to bet</Label>
                  <Input type="number" min="1" step="1" value={modal.amount} onChange={(e)=>setModal({...modal, amount:e.target.value})} data-testid="bet-amount-input" autoFocus />
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="preset-chips">
                    {[100, 500, 1000, 2000, 5000].map((v)=>(
                      <button
                        key={v} type="button" onClick={()=>addPreset(v)}
                        data-testid={`preset-${v}`}
                        className="px-3 py-1 rounded-full border border-border text-xs font-semibold mono bg-card hover:border-primary hover:bg-primary/10 btn-push"
                      >+{v}</button>
                    ))}
                    <button type="button" onClick={clearAmount} data-testid="preset-clear"
                      className="px-3 py-1 rounded-full border border-border text-xs font-semibold text-muted-foreground bg-card hover:border-red-500 hover:text-red-500 btn-push">Clear</button>
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-card space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Odds</span><span className="mono">{modal.event.odds}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Stake</span><span className="mono">{fmt(modal.amount || 0)}</span></div>
                  <div className="flex justify-between text-green-500"><span>If you win</span><span className="mono font-bold">+{fmt((parseFloat(modal.amount||0)*modal.event.odds) - parseFloat(modal.amount||0))}</span></div>
                  <div className="flex justify-between text-red-500"><span>If you lose</span><span className="mono font-bold">-{fmt(modal.amount || 0)}</span></div>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:justify-between">
                <div>
                  {modal.edit && (
                    <Button variant="destructive" onClick={cancelBet} data-testid="cancel-bet-btn">Cancel Bet</Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={()=>setModal(null)} data-testid="close-modal-btn">Close</Button>
                  <Button onClick={placeBet} disabled={betSubmitting} className="btn-push" data-testid="confirm-bet-btn">{betSubmitting ? "Working…" : (modal.edit ? "Update Bet" : "Confirm Bet")}</Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending bets (exposure) dialog */}
      <Dialog open={showExposure} onOpenChange={setShowExposure}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="exposure-dialog">
          <DialogHeader><DialogTitle>Pending bets · Exposure {fmt(balance.exposure)}</DialogTitle></DialogHeader>
          {(() => {
            // Derive pending bets from the events feed (each event carries the user's active my_bet).
            // This avoids needing to eagerly load /user/bet-statement.
            const pending = events
              .filter((ev) => ev.my_bet)
              .map((ev) => ({ ...ev.my_bet, event_team1: ev.team1, event_team2: ev.team2 }));
            if (pending.length === 0) return <div className="text-muted-foreground py-6 text-center">No pending bets right now.</div>;
            return (
              <Table>
                <TableHeader><TableRow><TableHead>Placed</TableHead><TableHead>Match</TableHead><TableHead>Team</TableHead><TableHead className="text-right">Stake</TableHead><TableHead className="text-right">Potential Win</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pending.map((b)=>(
                    <TableRow key={b.id} data-testid={`pending-bet-${b.id}`}>
                      <TableCell className="mono text-xs">{fmtDT(b.created_at)}</TableCell>
                      <TableCell>{b.event_team1} vs {b.event_team2}</TableCell>
                      <TableCell className="font-semibold">{b.team === "team1" ? b.event_team1 : b.event_team2}</TableCell>
                      <TableCell className="text-right mono">{fmt(b.amount)}</TableCell>
                      <TableCell className="text-right mono text-green-500">{fmt(b.potential_win)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Rules acceptance modal (blocking on first login) */}
      <Dialog open={rulesModal} onOpenChange={()=>{}}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="rules-modal" onEscapeKeyDown={(e)=>e.preventDefault()} onPointerDownOutside={(e)=>e.preventDefault()}>
          <DialogHeader><DialogTitle>Rules of the Book</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Please read the rules set by your operator. You must agree to them before you can play on this platform.</p>
          <pre className="whitespace-pre-wrap text-sm border border-border rounded p-3 bg-card mt-2 max-h-72 overflow-y-auto" data-testid="rules-modal-text">{user?.brand_rules || rules || "No rules set."}</pre>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={rulesAgree} onChange={(e)=>setRulesAgree(e.target.checked)} data-testid="rules-agree-checkbox" />
            I have read and I agree to abide by these rules.
          </label>
          <DialogFooter>
            <Button onClick={acceptRules} disabled={!rulesAgree} className="btn-push" data-testid="rules-accept-btn">Continue to platform</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcements auto-popup */}
      <Dialog open={annPopup} onOpenChange={(o)=>{ if (!o) closeAnnPopup(); else setAnnPopup(true); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ann-popup">
          <DialogHeader><DialogTitle>Announcements</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {announcements.map((a)=>(
              <div key={a.id} className="border border-accent/40 bg-accent/10 rounded-lg p-3">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.message}</div>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={closeAnnPopup} data-testid="ann-popup-close">Got it</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcements list (accessible via bell) */}
      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ann-list">
          <DialogHeader><DialogTitle>Announcements ({announcements.length})</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {announcements.length === 0 && <div className="text-muted-foreground text-center py-4">No active announcements.</div>}
            {announcements.map((a)=>(
              <div key={a.id} className="border border-border rounded-lg p-3">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.message}</div>
                <div className="text-[10px] text-muted-foreground mt-1 mono">Until {fmtDT(a.end_time)}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdrawal request dialog */}
      <Dialog open={withdrawModal} onOpenChange={(o)=>{ if(!o){ setWithdrawModal(false); setWithdrawAmount(""); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="withdraw-modal">
          <DialogHeader><DialogTitle>Request Withdrawal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3 bg-card text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Available balance</span><span className="mono font-bold">{fmt(balance.coin_balance)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Already locked</span><span className="mono text-blue-500">{fmt(balance.withdrawal_locked)}</span></div>
            </div>
            <div>
              <Label>Amount to withdraw</Label>
              <Input type="number" min="1" step="1" value={withdrawAmount} onChange={(e)=>setWithdrawAmount(e.target.value)} data-testid="withdraw-amount-input" autoFocus />
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="withdraw-presets">
                {[100, 500, 1000, 2000, 5000].map((v)=>(
                  <button key={v} type="button" onClick={()=>{
                    const cur = parseFloat(withdrawAmount) || 0;
                    setWithdrawAmount(String(cur + v));
                  }} data-testid={`withdraw-preset-${v}`}
                    className="px-3 py-1 rounded-full border border-border text-xs font-semibold mono bg-card hover:border-primary hover:bg-primary/10 btn-push">+{v}</button>
                ))}
                <button type="button" onClick={()=>setWithdrawAmount(String(balance.coin_balance || 0))} data-testid="withdraw-max"
                  className="px-3 py-1 rounded-full border border-border text-xs font-semibold mono bg-card hover:border-primary hover:bg-primary/10 btn-push">MAX</button>
                <button type="button" onClick={()=>setWithdrawAmount("")} data-testid="withdraw-clear"
                  className="px-3 py-1 rounded-full border border-border text-xs font-semibold text-muted-foreground bg-card hover:border-red-500 hover:text-red-500 btn-push">Clear</button>
              </div>
            </div>

            <div>
              <Label>Payment method</Label>
              <div className="grid grid-cols-3 gap-2 mt-1" data-testid="withdraw-methods">
                {[["upi","UPI"],["bank","Bank"],["other","Other"]].map(([k,l])=>(
                  <button key={k} type="button" onClick={()=>setWithdrawMethod(k)}
                    data-testid={`withdraw-method-${k}`}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold btn-push ${withdrawMethod===k?"border-primary bg-primary/10 text-primary":"border-border text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>

            {withdrawMethod === "upi" && (
              <div>
                <Label>UPI ID</Label>
                <Input value={withdrawPay.upi_id} onChange={(e)=>setWithdrawPay({...withdrawPay, upi_id:e.target.value})} placeholder="yourname@bank" data-testid="withdraw-upi-id" />
              </div>
            )}
            {withdrawMethod === "bank" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="md:col-span-2"><Label>Account holder name</Label><Input value={withdrawPay.account_holder} onChange={(e)=>setWithdrawPay({...withdrawPay, account_holder:e.target.value})} data-testid="withdraw-holder" /></div>
                <div><Label>Account number</Label><Input value={withdrawPay.account_number} onChange={(e)=>setWithdrawPay({...withdrawPay, account_number:e.target.value})} data-testid="withdraw-account-no" /></div>
                <div><Label>IFSC</Label><Input value={withdrawPay.ifsc} onChange={(e)=>setWithdrawPay({...withdrawPay, ifsc:e.target.value.toUpperCase()})} placeholder="HDFC0000123" data-testid="withdraw-ifsc" /></div>
                <div className="md:col-span-2"><Label>Bank name (optional)</Label><Input value={withdrawPay.bank_name} onChange={(e)=>setWithdrawPay({...withdrawPay, bank_name:e.target.value})} data-testid="withdraw-bank" /></div>
              </div>
            )}
            {withdrawMethod === "other" && (
              <div>
                <Label>Withdrawal method details</Label>
                <Input value={withdrawPay.notes} onChange={(e)=>setWithdrawPay({...withdrawPay, notes:e.target.value})} placeholder="e.g. PayPal id, Crypto wallet, etc." data-testid="withdraw-notes-other" />
              </div>
            )}

            {withdrawMethod !== "other" && (
              <div>
                <Label>Additional notes (optional)</Label>
                <Input value={withdrawPay.notes}
                  onChange={(e)=>setWithdrawPay({...withdrawPay, notes:e.target.value})}
                  placeholder="Any note for the admin"
                  data-testid="withdraw-notes" />
              </div>
            )}

            <p className="text-xs text-muted-foreground">Your coins will be locked until the admin approves or rejects your request. Rejected requests refund the coins to your balance.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setWithdrawModal(false); setWithdrawAmount(""); }} data-testid="withdraw-cancel-btn">Cancel</Button>
            <Button onClick={submitWithdrawal} disabled={withdrawSubmitting} className="btn-push" data-testid="withdraw-submit-btn">{withdrawSubmitting ? "Submitting…" : "Submit Request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Support floating icons */}
      {(user?.support_whatsapp || user?.support_telegram) && (
        <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-40" data-testid="support-icons">
          {user.support_whatsapp && (
            <a href={user.support_whatsapp} target="_blank" rel="noopener noreferrer"
              className="h-11 w-11 rounded-full grid place-items-center shadow-lg btn-push"
              style={{ background: "#25D366", color: "#fff" }}
              data-testid="support-whatsapp" title="WhatsApp support">
              <MessageCircle className="h-5 w-5" />
            </a>
          )}
          {user.support_telegram && (
            <a href={user.support_telegram} target="_blank" rel="noopener noreferrer"
              className="h-11 w-11 rounded-full grid place-items-center shadow-lg btn-push"
              style={{ background: "#229ED9", color: "#fff" }}
              data-testid="support-telegram" title="Telegram support">
              <Send className="h-5 w-5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
